import React from "react";
import {
  GripVertical,
  Copy,
  Trash2,
  ChevronUp,
  ChevronDown,
  Image as ImageIcon,
  Info,
  Lightbulb,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import { getBlockType, getBlockColors } from "./blocks.data";

const NOTE_STYLES = {
  info: { wrap: "bg-info_bg border-info_border", icon: "text-info", Icon: Info },
  tip: { wrap: "bg-primary_tint border-border", icon: "text-primary", Icon: Lightbulb },
  warning: { wrap: "bg-warning_bg border-warning_border", icon: "text-warning", Icon: AlertTriangle },
  success: { wrap: "bg-success_bg border-success_border", icon: "text-success", Icon: CheckCircle2 },
};

const HEADING_SIZE = { 1: "text-2xl", 2: "text-xl", 3: "text-lg", 4: "text-base" };
const ALIGN_CLASS = { left: "text-left", center: "text-center", right: "text-right" };
const IMAGE_SIZE_CLASS = { small: "max-w-[240px]", medium: "max-w-[420px]", large: "max-w-[640px]", full: "max-w-full" };
const IMAGE_ALIGN_CLASS = { left: "mr-auto", center: "mx-auto", right: "ml-auto" };
const GALLERY_COLS_CLASS = { 2: "grid-cols-2", 3: "grid-cols-3", 4: "grid-cols-2 sm:grid-cols-4" };

export const BlockPreview = ({ block }) => {
  const { type, data } = block;

  if (type === "heading") {
    const size = HEADING_SIZE[data.level] || HEADING_SIZE[2];
    return (
      <p className={`${size} font-bold text-text_primary tracking-tight ${ALIGN_CLASS[data.align] || "text-left"}`}>
        {data.text || "Untitled heading"}
      </p>
    );
  }

  if (type === "paragraph") {
    return (
      <div
        className="text-sm leading-relaxed text-text_secondary [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-primary [&_a]:underline [&_strong]:font-semibold [&_strong]:text-text_primary"
        dangerouslySetInnerHTML={{ __html: data.text || "" }}
      />
    );
  }

  if (type === "bulletList") {
    return (
      <ul className="list-disc pl-5 space-y-1.5 text-sm text-text_secondary marker:text-text_muted">
        {(data.items || []).map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    );
  }

  if (type === "numberedList") {
    return (
      <ol className="list-decimal pl-5 space-y-1.5 text-sm text-text_secondary marker:text-text_muted marker:font-semibold">
        {(data.items || []).map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ol>
    );
  }

  if (type === "image") {
    const sizeClass = IMAGE_SIZE_CLASS[data.size] || IMAGE_SIZE_CLASS.medium;
    const alignClass = IMAGE_ALIGN_CLASS[data.align] || IMAGE_ALIGN_CLASS.center;
    // "Full width" is meant to read as a banner/hero — it should always fill
    // the box edge-to-edge (cropping if needed), not letterbox a smaller
    // image inside empty space. Every other size keeps the whole image
    // visible (contain) since the user deliberately picked a bounded size.
    const isFull = data.size === "full";
    return (
      <div className={`${sizeClass} ${alignClass}`}>
        {data.src ? (
          <img
            src={data.src}
            alt={data.alt || ""}
            className={`w-full rounded-xl border border-border bg-surface_alt ${
              isFull ? "h-80 object-cover" : "max-h-80 object-contain"
            }`}
          />
        ) : (
          <div className="w-full h-40 rounded-xl border border-dashed border-border bg-surface_alt flex items-center justify-center">
            <ImageIcon size={26} className="text-text_muted" />
          </div>
        )}
        {data.caption && <p className="mt-2 text-[11.5px] text-text_muted text-center italic">{data.caption}</p>}
      </div>
    );
  }

  if (type === "gallery") {
    const images = data.images?.length ? data.images : [];
    const cols = GALLERY_COLS_CLASS[data.columns] || GALLERY_COLS_CLASS[2];
    return (
      <div className={`grid ${cols} gap-2.5`}>
        {images.map((img, i) => (
          <div key={i}>
            {img.src ? (
              <img
                src={img.src}
                alt={img.alt || ""}
                className="w-full aspect-square object-cover rounded-lg border border-border bg-surface_alt"
              />
            ) : (
              <div className="w-full aspect-square rounded-lg border border-dashed border-border bg-surface_alt flex items-center justify-center">
                <ImageIcon size={20} className="text-text_muted" />
              </div>
            )}
            {img.caption && (
              <p className="mt-1 text-[10.5px] text-text_muted text-center truncate">{img.caption}</p>
            )}
          </div>
        ))}
      </div>
    );
  }

  if (type === "code") {
    const lines = (data.code || "").split("\n");
    return (
      <div className="rounded-xl border border-border overflow-hidden shadow-sm">
        <div className="flex items-center gap-1.5 px-3.5 py-2 bg-surface_alt border-b border-border">
          <span className="w-2 h-2 rounded-full bg-danger/40" />
          <span className="w-2 h-2 rounded-full bg-warning/40" />
          <span className="w-2 h-2 rounded-full bg-success/40" />
          <span className="ml-2 text-[10.5px] font-semibold uppercase tracking-wide text-text_muted">
            {data.language}
          </span>
        </div>
        <pre className="px-4 py-3.5 bg-background text-[12.5px] text-text_primary overflow-x-auto">
          {data.showLineNumbers ? (
            <code>
              {lines.map((line, i) => (
                <div key={i} className="flex">
                  <span className="select-none w-7 flex-shrink-0 text-right pr-3 text-text_muted/50">{i + 1}</span>
                  <span className="whitespace-pre">{line || " "}</span>
                </div>
              ))}
            </code>
          ) : (
            <code>{data.code}</code>
          )}
        </pre>
      </div>
    );
  }

  if (type === "note") {
    const style = NOTE_STYLES[data.noteType] || NOTE_STYLES.info;
    const NoteIcon = style.Icon;
    return (
      <div className={`flex items-start gap-3 px-4 py-3.5 rounded-xl border ${style.wrap}`}>
        <NoteIcon size={16} className={`flex-shrink-0 mt-0.5 ${style.icon}`} />
        <div>
          {data.title && <p className={`text-[13px] font-semibold mb-0.5 ${style.icon}`}>{data.title}</p>}
          <p className="text-[13px] text-text_secondary leading-relaxed">{data.text}</p>
        </div>
      </div>
    );
  }

  return null;
};

const ActionButton = ({ onClick, disabled, title, children }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    title={title}
    className="p-1.5 rounded-md text-text_muted disabled:opacity-25 disabled:cursor-not-allowed"
  >
    {children}
  </button>
);

const BlockCard = ({
  block,
  index,
  total,
  isSelected,
  isDragging,
  isDragOver,
  onSelect,
  onDuplicate,
  onDelete,
  onMoveUp,
  onMoveDown,
  onDragStart,
  onDragOverBlock,
  onDrop,
  onDragEnd,
}) => {
  const blockType = getBlockType(block.type);
  const colors = getBlockColors(block.type);
  const TypeIcon = blockType.icon;

  return (
    <div
      onClick={() => onSelect(block.id)}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOverBlock(block.id);
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDrop(block.id);
      }}
      className={`relative rounded-2xl border bg-surface cursor-pointer shadow-sm ${
        isDragOver ? "border-primary ring-2 ring-primary/40" : isSelected ? `${colors.border} ring-2 ring-primary/15` : "border-border"
      } ${isDragging ? "opacity-40" : "opacity-100"}`}
    >
      <div className={`flex items-center gap-2.5 px-3.5 py-2.5 border-b ${isSelected ? "border-border" : "border-border"}`}>
        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-surface_alt text-text_muted text-[10px] font-bold flex items-center justify-center">
          {index + 1}
        </span>
        <span
          draggable
          onClick={(e) => e.stopPropagation()}
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", String(block.id));
            onDragStart(block.id);
          }}
          onDragEnd={onDragEnd}
          title="Drag to reorder"
          className="flex-shrink-0 cursor-grab active:cursor-grabbing"
        >
          <GripVertical size={14} className="text-text_muted" />
        </span>
        <div className={`flex items-center gap-1.5 flex-shrink-0 px-2 py-0.5 rounded-md ${colors.bg}`}>
          <TypeIcon size={12} className={colors.text} />
          <span className={`text-[10.5px] font-bold uppercase tracking-wide ${colors.text}`}>{blockType.label}</span>
        </div>

        <div className="flex-1" />

        <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
          <ActionButton disabled={index === 0} onClick={() => onMoveUp(block.id)} title="Move up">
            <ChevronUp size={14} />
          </ActionButton>
          <ActionButton disabled={index === total - 1} onClick={() => onMoveDown(block.id)} title="Move down">
            <ChevronDown size={14} />
          </ActionButton>
          <ActionButton onClick={() => onDuplicate(block.id)} title="Duplicate">
            <Copy size={13} />
          </ActionButton>
          <button
            type="button"
            onClick={() => onDelete(block.id)}
            title="Delete"
            className="p-1.5 rounded-md text-danger"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      <div className="px-4 py-4 min-w-0 break-normal [overflow-wrap:break-word] [word-break:normal] [hyphens:none]">
        <BlockPreview block={block} />
      </div>
    </div>
  );
};

export default BlockCard;
